import { gql } from '@apollo/client';

/**
 * Cash Flow write side (ТЗ 0023).
 *
 * `setBankTransactionCategory` returns what the system *learned* alongside what
 * it changed — a rule created, a rule suggested, a conflict, or an explicit
 * reason why nothing could be learned. The UI must surface that; a silent "it
 * worked" is how the learning stops being trusted.
 */
/**
 * Loading a statement — the first step of the loop, and the one that was
 * missing: until this existed the only way to get money into the ledger was a
 * management command on the server.
 *
 * `dryRun` defaults to true on the server. The UI always previews first and
 * commits as a second, explicit call.
 */
export const UPLOAD_BANK_STATEMENT = gql`
  mutation UploadBankStatement(
    $file: Upload!
    $dryRun: Boolean
    $accountSlug: String
  ) {
    uploadBankStatement(
      file: $file
      dryRun: $dryRun
      accountSlug: $accountSlug
    ) {
      success
      errors
      dryRun
      alreadyImported
      accountName
      accountIbanLearned
      processingStats
      run {
        pk
        fileName
        status
        rowsTotal
        createdCount
        duplicateCount
        failedCount
        balanceGapCount
        periodFrom
        periodTo
        report
      }
    }
  }
`;

/** The operator answering «запомнить это правило?» with yes. */
export const CONFIRM_CASH_FLOW_RULE = gql`
  mutation ConfirmCashFlowRule(
    $transactionId: ID!
    $categorySlug: String!
    $retroactive: Boolean
  ) {
    confirmCashFlowRule(
      transactionId: $transactionId
      categorySlug: $categorySlug
      retroactive: $retroactive
    ) {
      success
      errors
      ruleOutcome
      ruleReason
      rule {
        pk
        keyKind
        keyValue
        categoryName
        validFrom
      }
    }
  }
`;

/**
 * Settling «этот признак уже ведёт в другую категорию».
 *
 * `REPLACE` closes the old rule at today's date, so rows booked earlier keep
 * the category they already had — a closed month cannot move.
 */
export const RESOLVE_CASH_FLOW_RULE_CONFLICT = gql`
  mutation ResolveCashFlowRuleConflict(
    $transactionId: ID!
    $categorySlug: String!
    $strategy: String!
  ) {
    resolveCashFlowRuleConflict(
      transactionId: $transactionId
      categorySlug: $categorySlug
      strategy: $strategy
    ) {
      success
      errors
      ruleOutcome
      ruleReason
      rule {
        pk
        categoryName
        validFrom
        validTo
      }
    }
  }
`;

export const UPDATE_CASH_FLOW_RULE = gql`
  mutation UpdateCashFlowRule(
    $id: ID!
    $isActive: Boolean
    $priority: Int
    $categorySlug: String
    $resetHealth: Boolean
  ) {
    updateCashFlowRule(
      id: $id
      isActive: $isActive
      priority: $priority
      categorySlug: $categorySlug
      resetHealth: $resetHealth
    ) {
      success
      errors
      rule {
        pk
        isActive
        priority
        categorySlug
        categoryName
        health
        overridesCount
      }
    }
  }
`;

export const DELETE_CASH_FLOW_RULE = gql`
  mutation DeleteCashFlowRule($id: ID!) {
    deleteCashFlowRule(id: $id) {
      success
      errors
      stats
    }
  }
`;

export const UPDATE_CASH_FLOW_SETTINGS = gql`
  mutation UpdateCashFlowSettings(
    $ownPartyNames: String
    $internalTransferAutodetect: Boolean
    $internalTransferWindowDays: Int
  ) {
    updateCashFlowSettings(
      ownPartyNames: $ownPartyNames
      internalTransferAutodetect: $internalTransferAutodetect
      internalTransferWindowDays: $internalTransferWindowDays
    ) {
      success
      errors
      settings {
        ownPartyNames
        internalTransferAutodetect
        internalTransferWindowDays
      }
    }
  }
`;

export const SET_BANK_TRANSACTION_CATEGORY = gql`
  mutation SetBankTransactionCategory(
    $id: ID!
    $categorySlug: String
    $learn: Boolean
  ) {
    setBankTransactionCategory(
      id: $id
      categorySlug: $categorySlug
      learn: $learn
    ) {
      success
      errors
      ruleOutcome
      ruleReason
      rule {
        pk
        keyKind
        keyValue
        categoryName
      }
      conflictingRule {
        pk
        categoryName
      }
      preview {
        keyKind
        keyValue
        direction
        wouldAffect
        samples
      }
      transaction {
        pk
        categorySlug
        categoryName
        categorySource
      }
    }
  }
`;

export const SET_BANK_TRANSACTION_INTERNAL = gql`
  mutation SetBankTransactionInternal($id: ID!, $isInternal: Boolean!) {
    setBankTransactionInternal(id: $id, isInternal: $isInternal) {
      success
      errors
      transaction {
        pk
        isInternalTransfer
        internalSource
        needsReview
      }
    }
  }
`;

/**
 * «Обработать загруженное» — четыре шага одним вызовом.
 *
 * Порядок между распознаванием выплат шлюза и сопоставлением документов
 * обязателен и задан на сервере, поэтому здесь его повторять не нужно и нельзя:
 * четыре отдельных вызова подряд с фронта воспроизвели бы старую ловушку.
 */
export const PROCESS_UPLOADED_STATEMENT = gql`
  mutation ProcessUploadedStatement($dryRun: Boolean) {
    processUploadedStatement(dryRun: $dryRun) {
      success
      errors
      dryRun
      stats
    }
  }
`;

export const RUN_CASH_FLOW_CATEGORIZATION = gql`
  mutation RunCashFlowCategorization($dryRun: Boolean) {
    runCashFlowCategorization(dryRun: $dryRun) {
      success
      errors
      dryRun
      stats
    }
  }
`;

export const RUN_INTERNAL_TRANSFER_DETECTION = gql`
  mutation RunInternalTransferDetection($dryRun: Boolean) {
    runInternalTransferDetection(dryRun: $dryRun) {
      success
      errors
      dryRun
      stats
    }
  }
`;

export const RUN_CASH_FLOW_MATCHING = gql`
  mutation RunCashFlowMatching($dryRun: Boolean) {
    runCashFlowMatching(dryRun: $dryRun) {
      success
      errors
      dryRun
      stats
    }
  }
`;

export const SEED_CASH_FLOW_RULES = gql`
  mutation SeedCashFlowRules($dryRun: Boolean) {
    seedCashFlowRules(dryRun: $dryRun) {
      success
      errors
      dryRun
      stats
    }
  }
`;

export const DETECT_GATEWAY_SETTLEMENTS = gql`
  mutation DetectGatewaySettlements($dryRun: Boolean) {
    detectGatewaySettlements(dryRun: $dryRun) {
      success
      errors
      dryRun
      stats
    }
  }
`;

export const ADD_GATEWAY_SETTLEMENT_ITEM = gql`
  mutation AddGatewaySettlementItem($settlementId: ID!, $invoiceId: ID!) {
    addGatewaySettlementItem(
      settlementId: $settlementId
      invoiceId: $invoiceId
    ) {
      success
      errors
      settlement {
        pk
        itemsTotal
        feeAmount
        unallocated
        status
        items {
          pk
          invoiceId
          invoiceNumber
          amount
          matchSource
        }
      }
    }
  }
`;

export const REMOVE_GATEWAY_SETTLEMENT_ITEM = gql`
  mutation RemoveGatewaySettlementItem($itemId: ID!) {
    removeGatewaySettlementItem(itemId: $itemId) {
      success
      errors
      settlement {
        pk
        itemsTotal
        feeAmount
        unallocated
        status
        items {
          pk
          invoiceId
          invoiceNumber
          amount
          matchSource
        }
      }
    }
  }
`;

export const MARK_GATEWAY_SETTLEMENT_RECONCILED = gql`
  mutation MarkGatewaySettlementReconciled(
    $settlementId: ID!
    $notes: String!
  ) {
    markGatewaySettlementReconciled(
      settlementId: $settlementId
      notes: $notes
    ) {
      success
      errors
      settlement {
        pk
        status
        notes
      }
    }
  }
`;

/** Attach a document to a bank row by hand — the primary path for expenses. */
export const LINK_BANK_TRANSACTION = gql`
  mutation LinkBankTransaction(
    $transactionId: ID!
    $targetType: String!
    $targetId: ID!
  ) {
    linkBankTransaction(
      transactionId: $transactionId
      targetType: $targetType
      targetId: $targetId
    ) {
      success
      errors
      transaction {
        pk
        links {
          pk
          targetType
          targetId
          targetLabel
          matchSource
        }
      }
    }
  }
`;

export const UNLINK_BANK_TRANSACTION = gql`
  mutation UnlinkBankTransaction($linkId: ID!) {
    unlinkBankTransaction(linkId: $linkId) {
      success
      errors
      transaction {
        pk
        links {
          pk
          targetType
          targetId
          targetLabel
          matchSource
        }
      }
    }
  }
`;

/**
 * Classify a selection in one decision.
 *
 * Learning is collapsed by signal on the server: twenty rows of one contractor
 * teach ONE rule. Sending N single mutations would return nineteen «правило уже
 * было» answers to a single decision.
 */
export const SET_BANK_TRANSACTION_CATEGORY_BULK = gql`
  mutation SetBankTransactionCategoryBulk(
    $ids: [ID!]!
    $categorySlug: String
    $learn: Boolean
  ) {
    setBankTransactionCategoryBulk(
      ids: $ids
      categorySlug: $categorySlug
      learn: $learn
    ) {
      success
      errors
      stats
    }
  }
`;

export const SET_GATEWAY_SETTLEMENT_FEE = gql`
  mutation SetGatewaySettlementFee($settlementId: ID!, $feeAmount: Float!) {
    setGatewaySettlementFee(
      settlementId: $settlementId
      feeAmount: $feeAmount
    ) {
      success
      errors
      settlement {
        pk
        feeAmount
        feeSource
        unallocated
        status
      }
    }
  }
`;

export const RECONCILE_GATEWAY_SETTLEMENTS = gql`
  mutation ReconcileGatewaySettlements($dryRun: Boolean) {
    reconcileGatewaySettlements(dryRun: $dryRun) {
      success
      errors
      dryRun
      stats
    }
  }
`;

/** Откат одной ЗАПИСАННОЙ загрузки. Сухой прогон обязателен по устройству:
 *  сервер сначала называет число строк, и только потом их удаляет. */
export const ROLLBACK_BANK_STATEMENT_IMPORT = gql`
  mutation RollbackBankStatementImport($importId: ID!, $dryRun: Boolean) {
    rollbackBankStatementImport(importId: $importId, dryRun: $dryRun) {
      success
      errors
      dryRun
      stats
    }
  }
`;

/** Снять флаг «ждёт разбора». Единственный способ уменьшить счётчик, который
 *  до этого умел только расти. */
export const CLEAR_BANK_TRANSACTION_REVIEW = gql`
  mutation ClearBankTransactionReview($id: ID!) {
    clearBankTransactionReview(id: $id) {
      success
      errors
      transaction {
        pk
        needsReview
      }
    }
  }
`;
